import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { UseOptions, useOptions } from "PFCore/hooks/queries/options";
import { Id, Option } from "PFTypes";

import { SelectV2Props } from "../select_v2.types";
import { flattenOptions } from "../select_v2.utils";

type UseSelectedOptions<T> = { selectedOptionIds: Id[] } & Pick<
  SelectV2Props<T>,
  "options" | "fetchOptions" | "limit"
> &
  Pick<UseOptions<T>, "queryOptions" | "dropdownId">;

type UseSelectedOptionsReturn<T> = {
  data: Option<T>[];
  isFetching: boolean;
  limit?: number;
  isLimitReached: boolean;
};

export const useSelectedOptions = <T>({
  selectedOptionIds,
  options,
  fetchOptions,
  queryOptions,
  dropdownId,
  limit: uiLimit
}: UseSelectedOptions<T>): UseSelectedOptionsReturn<T> => {
  const enabled = !isEmpty(selectedOptionIds) && (!!queryOptions?.enabled || isNil(queryOptions?.enabled));
  const {
    data: availableOptions,
    isFetching,
    limit: apiLimit
  } = useOptions({
    dropdownId,
    ids: selectedOptionIds,
    options,
    fetchOptions,
    queryOptions: {
      ...queryOptions,
      enabled
    }
  });

  const limit = Math.min(apiLimit ?? Infinity, uiLimit ?? Infinity);

  const selectedOptions = flattenOptions(availableOptions).filter(({ id }) => selectedOptionIds.includes(id));
  const isLimitReached = !!limit && selectedOptionIds.length >= limit;

  return {
    data: selectedOptions,
    isFetching: isEmpty(selectedOptionIds) ? false : isFetching,
    limit,
    isLimitReached
  };
};
